import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GUIDES_DATA, GUIDE_CATEGORIES, parseGuideDate } from '../data/guidesData';

const GuidesPage = ({ agent, onGoBack }) => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All Guides');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Johor Bahru Property Guides & Expert Buying Tips | Nicole Pua Property Agent";

    // Set meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      'Comprehensive Johor Bahru property guides: Singaporean buyer rules, MM2H vs PR, MRTA vs MLTA insurance, stamp duty exemptions, and Feng Shui tips by Nicole Pua.'
    );

    // Set canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://nicolejbproperty.com/guides');

    // Inject ItemList JSON-LD schema
    const schemaId = 'guides-itemlist-schema';
    let schemaScript = document.getElementById(schemaId);
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = schemaId;
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Johor Bahru Property Guides",
      "description": "Expert advice, legal fees breakdown, and property buying guides in Johor Bahru.",
      "itemListElement": GUIDES_DATA.map((guide, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": guide.title,
        "url": `https://nicolejbproperty.com/guides/${guide.slug}`
      }))
    };
    schemaScript.textContent = JSON.stringify(itemListSchema);

    return () => {
      if (schemaScript) schemaScript.remove();
    };
  }, []);

  const handleGuideClick = (slug) => {
    navigate(`/guides/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredGuides = GUIDES_DATA.filter((guide) => {
    const matchesCategory =
      selectedCategory === 'All Guides' || guide.category === selectedCategory;

    if (!searchQuery.trim()) return matchesCategory;

    const q = searchQuery.toLowerCase().trim();
    const matchesQuery =
      guide.title.toLowerCase().includes(q) ||
      guide.summary.toLowerCase().includes(q) ||
      (guide.highlights && guide.highlights.some(h => h.toLowerCase().includes(q)));

    return matchesCategory && matchesQuery;
  }).sort((a, b) => parseGuideDate(b.date) - parseGuideDate(a.date));

  return (
    <div className="guides-page">
      {/* Hero Header Section */}
      <div 
        className="area-hero-section"
        style={{
          backgroundImage: `linear-gradient(rgba(9, 13, 22, 0.88), rgba(16, 24, 38, 0.92)), url(https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1920&q=80)`
        }}
      >
        <div className="area-hero-content">
          <span className="area-hero-tag"><i className="bi bi-journal-bookmark-fill"></i> KNOWLEDGE BASE</span>
          <h1 className="area-hero-title">JOHOR BAHRU PROPERTY GUIDES & INSIGHTS</h1>
          <p className="area-hero-desc">
            Expert real estate guides, legal procedures, stamp duty breakdowns, and buyer insights curated by Nicole Pua Property Consultancy.
          </p>
        </div>
      </div>

      {/* Main Container */}
      <div className="area-container">
        {/* Breadcrumb Navigation */}
        <div className="breadcrumbs">
          <span className="breadcrumb-item" onClick={() => { navigate('/'); window.scrollTo(0, 0); }}>
            Home
          </span>
          <span className="breadcrumb-separator"><i className="bi bi-chevron-right"></i></span>
          <span className="breadcrumb-item active">Property Guides</span>
        </div>

        <div className="area-page-header">
          <div>
            <p className="properties-section-subtitle" style={{ marginTop: 0 }}>
              Clear, honest, step-by-step guides to help you buy, sell, or rent real estate in JB with confidence.
            </p>
          </div>
          <span className="properties-found-count">
            {filteredGuides.length} {filteredGuides.length === 1 ? 'Article' : 'Articles'}
          </span>
        </div>

        {/* Search & Category Filter Section */}
        <div className="guides-filter-section">
          {/* Search Bar */}
          <div className="township-search-container">
            <i className="bi bi-search township-search-icon"></i>
            <input
              type="text"
              className="township-search-input"
              placeholder="Search guides (e.g. Singaporean buyer, RTS link, stamp duty, Bukit Indah...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="township-search-clear"
                onClick={() => setSearchQuery('')}
                title="Clear search"
                aria-label="Clear search"
              >
                <i className="bi bi-x-circle-fill"></i>
              </button>
            )}
          </div>

          {/* Category Tabs */}
          <div className="guides-category-tabs">
            {GUIDE_CATEGORIES.map((cat) => (
              <button
                key={cat}
                className={`category-tab-btn ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Guides Grid or Empty State */}
        {filteredGuides.length === 0 ? (
          <div className="township-empty-state">
            <div className="township-empty-icon">
              <i className="bi bi-file-earmark-text"></i>
            </div>
            <div className="township-empty-title">No Guides Found</div>
            <p>No property guides match "{searchQuery}". Try selecting another category or clear your search term.</p>
            <button className="btn-clear-search" onClick={() => { setSearchQuery(''); setSelectedCategory('All Guides'); }}>
              RESET FILTERS
            </button>
          </div>
        ) : (
          <div className="guides-grid">
            {filteredGuides.map((guide) => (
              <article
                key={guide.id}
                className="guide-card"
                onClick={() => handleGuideClick(guide.slug)}
                style={{ cursor: 'pointer' }}
              >
                <div className="guide-card-image-wrapper">
                  <img src={guide.coverImage} alt={guide.title} className="guide-card-image" />
                  <span className="guide-category-badge">{guide.category}</span>
                </div>

                <div className="guide-card-body">
                  <div className="guide-card-meta">
                    <span><i className="bi bi-calendar3"></i> {guide.date}</span>
                    <span className="meta-divider">•</span>
                    <span><i className="bi bi-clock"></i> {guide.readTime}</span>
                  </div>

                  <h3 className="guide-card-title">{guide.title}</h3>
                  <p className="guide-card-summary">{guide.summary}</p>

                  <div className="guide-card-footer">
                    <button
                      className="btn-read-guide"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGuideClick(guide.slug);
                      }}
                    >
                      READ GUIDE <i className="bi bi-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuidesPage;
